import type { Product, LineItem } from './shopwareApi';

type CalculatedPriceLike = {
  unitPrice?: number;
  referencePrice?: { price?: number };
  listPrice?: { price?: number };
};

/**
 * Prefer rule-based calculatedPrices[0] when present; otherwise fall back to calculatedPrice.
 */
export const getEffectiveCalculatedPrice = (
  product: Product | null | undefined
): CalculatedPriceLike | null | undefined => {
  if (!product) return undefined;
  const calculatedPrices = (product as Product & { calculatedPrices?: CalculatedPriceLike[] }).calculatedPrices;
  if (Array.isArray(calculatedPrices) && calculatedPrices.length > 0) {
    return calculatedPrices[0];
  }
  return product.calculatedPrice ?? undefined;
};

/**
 * Format a price number for display with German locale formatting
 * @param price - The price to format (can be undefined)
 * @returns Formatted price string (e.g., "12,99") or "-" if not available
 */
export const formatPrice = (price: number | undefined): string => {
  if (price === undefined || price === null) return '-';
  return price.toFixed(2).replace('.', ',');
};

/**
 * Format date from ISO string to DD.MM.YYYY
 * @param dateString - The ISO date string to format (can be undefined)
 * @returns Formatted date string (e.g., "26.01.2026") or "-" if not available
 */
export const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

/**
 * Get formatted unit price with currency symbol from a Shopware product
 * @param product - The Shopware product (can be null/undefined)
 * @param fallback - Fallback text to show if price is not available (default: "-")
 * @returns Formatted price with currency (e.g., "12,99 €")
 */
export const getFormattedUnitPrice = (product: Product | null | undefined, fallback: string = '-'): string => {
  const calculatedPrice = getEffectiveCalculatedPrice(product);
  if (!calculatedPrice?.unitPrice) return fallback;
  return `${formatPrice(calculatedPrice.unitPrice)} €`;
};

/**
 * Get formatted reference price with currency and unit from a Shopware product
 * @param product - The Shopware product (can be null/undefined)
 * @param fallback - Fallback text to show if reference price is not available (default: "-")
 * @returns Formatted reference price with currency and unit (e.g., "1,29 €/kg")
 */
export const getFormattedReferencePrice = (product: Product | null | undefined, fallback: string | null = null): string | null => {
  const calculatedPrice = getEffectiveCalculatedPrice(product);
  if (!calculatedPrice?.referencePrice?.price) return fallback;

  const price = formatPrice(calculatedPrice.referencePrice.price);
  const unitName = product.unit?.translated?.shortCode || product.unit?.shortCode || '-';

  return `${price} €/${unitName}`;
};

/**
 * Get formatted list price (original price before discount) from a Shopware product
 * @param product - The Shopware product (can be null/undefined)
 * @returns Formatted list price with currency (e.g., "69,99 €") or null if no list price exists
 */
export const getFormattedListPrice = (product: Product | null | undefined): string | null => {
  const listPrice = getEffectiveCalculatedPrice(product)?.listPrice?.price;
  //if (!listPrice) return Math.random() < 1 ? `${formatPrice(99.99)} €` : null;
  if (!listPrice) return null;
  return `${formatPrice(listPrice)} €`;
};

/**
 * Get both formatted unit price and reference price from a Shopware product
 * @param product - The Shopware product (can be null/undefined)
 * @param fallbacks - Optional fallback values for unit and reference prices
 * @returns Object with formatted unitPrice, referencePrice, listPrice (null if no discount), and unitName
 */
export const getFormattedPrices = (
  product: Product | null | undefined,
  fallbacks?: { unitPrice?: string; referencePrice?: string | null }
): {
  unitPrice: string;
  referencePrice: string | null;
  listPrice: string | null;
  unitName: string;
} => {
  const unitPrice = getFormattedUnitPrice(product, fallbacks?.unitPrice);
  const referencePrice = getFormattedReferencePrice(product, fallbacks?.referencePrice);
  const listPrice = getFormattedListPrice(product);
  const unitName = product?.unit?.translated?.shortCode || product?.unit?.shortCode || '-';

  return {
    unitPrice,
    referencePrice,
    listPrice,
    unitName,
  };
};

/**
 * Check product availability based on Shopware product data
 * @param product - The Shopware product to check
 * @returns Boolean indicating if product is available
 */
export const checkProductAvailability = (product: Product | null): boolean => {
  if (!product) {
    return false;
  }

  // Check if product is available for purchase (must be active, available, and in stock)
  return !!(product.available && product.availableStock && product.availableStock > 0 && product.active);
};

/**
 * Get formatted unit price with currency symbol from a cart LineItem
 * @param lineItem - The Shopware LineItem (can be null/undefined)
 * @param fallback - Fallback text to show if price is not available (default: "-")
 * @returns Formatted price with currency (e.g., "12,99 €")
 */
export const getFormattedLineItemUnitPrice = (
  lineItem: LineItem | null | undefined,
  fallback: string = '-'
): string => {
  if (!lineItem?.price?.unitPrice) return fallback;
  return `${formatPrice(lineItem.price.unitPrice)} €`;
};

/**
 * Get formatted total price with currency symbol from a cart LineItem
 * @param lineItem - The Shopware LineItem (can be null/undefined)
 * @param fallback - Fallback text to show if price is not available (default: "-")
 * @returns Formatted price with currency (e.g., "25,98 €")
 */
export const getFormattedLineItemTotalPrice = (
  lineItem: LineItem | null | undefined,
  fallback: string = '-'
): string => {
  if (!lineItem?.price?.totalPrice) return fallback;
  return `${formatPrice(lineItem.price.totalPrice)} €`;
};

/**
 * Get formatted reference price with currency and unit from a cart LineItem
 * @param lineItem - The Shopware LineItem (can be null/undefined)
 * @param fallback - Fallback text to show if reference price is not available (default: "-")
 * @returns Formatted reference price with currency and unit (e.g., "1,29 €/kg")
 */
export const getFormattedLineItemReferencePrice = (
  lineItem: LineItem | null | undefined,
  fallback: string | null = null
): string | null => {
  const referencePriceValue = (lineItem?.price as any)?.referencePrice?.price;
  if (!referencePriceValue) return fallback;

  const price = formatPrice(referencePriceValue);
  const unitName =
    (lineItem?.price as any)?.referencePrice?.unitName ||
    (lineItem as any)?.deliveryInformation?.unit?.translated?.shortCode ||
    (lineItem as any)?.deliveryInformation?.unit?.shortCode ||
    '-';

  return `${price} €/${unitName}`;
};

/**
 * Get raw numeric list price (original price before discount) from a cart LineItem
 * @param lineItem - The Shopware LineItem (can be null/undefined)
 * @returns Raw list price as number or null if no list price exists
 */
const getRawLineItemListPrice = (lineItem: LineItem | null | undefined): number | null => {
  const listPrice = (lineItem?.price as any)?.listPrice?.price;
  //if (!listPrice) return Math.random() < 1 ? 99.99 : null;
  if (!listPrice) return null;
  return listPrice;
};

/**
 * Get all formatted prices from a cart LineItem
 * @param lineItem - The Shopware LineItem (can be null/undefined)
 * @param fallbacks - Optional fallback values for unit, total, and reference prices
 * @returns Object with formatted unitPrice, totalPrice, referencePrice, listPrice (null if no discount), and unitName
 */
export const getFormattedLineItemPrices = (
  lineItem: LineItem | null | undefined,
  fallbacks?: { unitPrice?: string; totalPrice?: string; referencePrice?: string | null }
): {
  unitPrice: string;
  totalPrice: string;
  referencePrice: string | null;
  listPrice: string | null;
  totalListPrice: string | null;
  unitName: string;
} => {
  const unitPrice = getFormattedLineItemUnitPrice(lineItem, fallbacks?.unitPrice);
  const totalPrice = getFormattedLineItemTotalPrice(lineItem, fallbacks?.totalPrice);
  const referencePrice = getFormattedLineItemReferencePrice(lineItem, fallbacks?.referencePrice);

  // listPrice (per-unit) and totalListPrice (per-unit * quantity) for mobile views
  const rawListPrice = getRawLineItemListPrice(lineItem);
  const listPrice = rawListPrice !== null ? `${formatPrice(rawListPrice)} €` : null;
  const quantity = lineItem?.quantity || 1;
  const totalListPrice = rawListPrice !== null ? `${formatPrice(rawListPrice * quantity)} €` : null;

  const unitName =
    (lineItem?.price as any)?.referencePrice?.unitName ||
    (lineItem as any)?.deliveryInformation?.unit?.translated?.shortCode ||
    (lineItem as any)?.deliveryInformation?.unit?.shortCode ||
    '-';

  return {
    unitPrice,
    totalPrice,
    referencePrice,
    listPrice,
    totalListPrice,
    unitName,
  };
};
